import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Link } from 'navigation'
import { Button, UploadInput, Dropdown } from 'components/UIElements'
import { replaceInString } from 'utils/misc'
import { PENDING_CONSENTS, COMPLETED_CONSENTS, SESSION_STORAGE_KEYS } from 'utils/constants'
import { logSentryEvent, SENTRY_MESSAGES } from '../../../../../utils/sentry'
import '../stylesheets/ProfilePage.scss'
import ConsentList from '../../../../Consent/components/ConsentList'
import ParticipantSelectionOverlay from 'components/ParticipantSelectionOverlay'
import BlockingConsentModal from '../../../../Consent/components/BlockingConsentModal'
import { getCaregiverSelectionFlags } from 'utils/caregiverUtils'

const getFileTypeOptionsStrings = strings => {
  return [
    { key: 'selectOne', text: strings.select_one },
    {
      key: 'medicalHistory',
      value: 'Medical History',
      text: strings.medical_history
    },
    {
      key: 'familyHistory',
      value: 'Family History',
      text: strings.family_history
    },
    {
      key: 'autopsyReport',
      value: 'Autopsy Report',
      text: strings.autopsy_report
    },
    { key: 'pedigree', value: 'Pedigree', text: strings.pedigree },
    { key: 'other', value: 'Other', text: strings.other }
  ]
}

const renderAccountSection = (
  label,
  contents,
  breakAll = false,
  hasSeparator = false // hasSeparator = true will add a border and padding to the bottom
) => {
  return (
    <div
      className={`flexed${breakAll ? ' break-all' : ''}${hasSeparator ? ' separator' : ''
        }`}>
      <div>
        <div className='data-label'>{label}</div>
      </div>
      <div className='data-content'>{contents}</div>
    </div>
  )
}

const ProfilePage = props => {
  const {
    authData,
    consentRequired,
    downloadConsentPDF,
    downloadSignConsentPDF,
    email,
    username,
    files,
    firstName,
    selectedLocale,
    localesList,
    selectLanguage,
    siteName,
    strings,
    studyID,
    uploadFile,
    completedConsents,
    pendingConsents,
    v2Consents,
    onFetchConsentDetails,
    showConsentModal
  } = props
  const [file, setFile] = useState({})
  const [fileType, setFileType] = useState('selectOne')
  const [fileTypeVal, setFileTypeVal] = useState(null)
  const form = document.forms.namedItem('file-upload')
  const { id } = authData
  const hasFile = !!file.name

  useEffect(() => {
    logSentryEvent({
      message: SENTRY_MESSAGES.userProfileScreenViewed,
      tags: { flow: 'profile', action: 'screen_viewed' },
    })
  }, [])

  const getFileType = selectedFile => {
    if (selectedFile) {
      const { type } = selectedFile
      const _fileType = type ? type.slice(type.indexOf('/') + 1) : null
      return _fileType
    }
    return null
  }

  const resetFile = () => {
    setFile({})
  }

  const onFileSelection = selectedFile => {
    resetFile()
    setFile(selectedFile)
  }

  const onFileUpload = () => {
    const fData = new FormData(form)
    fData.set('study_id', studyID)
    fData.set('upload_file', file)
    fData.set('participant_id', id)
    fData.set('tag_name', fileTypeVal)
    fData.set('file_name', file.name)
    uploadFile(
      fData,
      strings.upload_file_success,
      strings.upload_file_error
    ).then(() => {
      resetFile()
    })
  }

  const renderFileList = _strings => {
    const filesArray = files ?? []
    let num = 0
    const filesList = filesArray.map(_file => {
      const { fileName: _fileName, fileType: _fileType } = _file
      num += 1
      return (
        <div className='flexed uploaded-file' key={`file-${num}`}>
          <span className='bold'>{_strings[_fileType]}</span>
          <span>{_fileName}</span>
        </div>
      )
    })
    if (filesArray.length) return <div className='has-max-height'>{filesList}</div>
    return null
  }

  const renderForm = () => {
    return (
      <form encType='mulitpart/form-data' name='file-upload'>
        <UploadInput
          file={file}
          accept={['.png', '.jpg', '.jpeg', '.pdf', '.docx', '.doc']}
          onUpdateFile={onFileSelection}
          name='upload_file'
          type={getFileType(file)}
          strings={strings}
        />
        {hasFile && (
          <div>
            <p>{strings.file_type}</p>
            <Dropdown
              selected={fileType}
              options={getFileTypeOptionsStrings(strings)}
              onSelect={item => {
                setFileType(item.key)
                setFileTypeVal(item.value)
              }}
            />
            <div className='flexed file-save'>
              <Button
                type='button'
                disabled={!fileTypeVal}
                content={strings.save_btn}
                onClick={() => {
                  onFileUpload()
                }}
              />
              <Button
                link
                content={strings.cancel_btn}
                onClick={() => {
                  resetFile()
                }}
              />
            </div>
          </div>
        )}
      </form>
    )
  }

  return (
    <div className='profile page'>
      <BlockingConsentModal
        showConsentModal={showConsentModal}
        strings={strings}
      />
      {!showConsentModal && (
        <>
          {(() => {
            const { mustSelectParticipant } = getCaregiverSelectionFlags({
              user: props?.user,
              caregiver: props?.caregiver
            })
            return mustSelectParticipant ? <ParticipantSelectionOverlay /> : null
          })()}
          <div className='top flexed'>
            <div className='left'>
              <p className='section-label'>{strings.files}</p>
              <div className='linkt-container'>
                {renderForm()}
                {renderFileList(strings)}
              </div>
            </div>
            <div className='right'>
              {consentRequired && (
                <div>
                  <p className='section-label'>{strings.completed_consents || COMPLETED_CONSENTS}</p>
                  <div className='linkt-container'>
                    <ConsentList studyID={studyID}
                      consent_status='completed'
                      strings={strings}
                      onFetchConsentDetails={onFetchConsentDetails}
                      downloadConsentPDF={downloadConsentPDF}
                      downloadSignConsentPDF={downloadSignConsentPDF}
                      consents={completedConsents} />
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className='bottom flexed'>
            <div className='left'>
              <p className='section-label'>{strings.account}</p>
              <div className='linkt-container'>
                {renderAccountSection(
                  email ? strings.email_label : strings.username,
                  email || username,
                  true
                )}
                {!!siteName && renderAccountSection(strings.site, siteName)}
                {renderAccountSection(
                  strings.password,
                  <Link to='/profile/change-password'>
                    {strings.change_password}
                  </Link>
                )}
              </div>
            </div>
            {pendingConsents.length > 0 && (
              <div className='right'>
                <p className='section-label  pending-consents-label'>{strings.pending_consents || PENDING_CONSENTS}</p>
                {(
                  <div>
                    <div className='linkt-container pending-consents-box'> <ConsentList
                      studyID={studyID}
                      consent_status='pending'
                      strings={strings}
                      onFetchConsentDetails={onFetchConsentDetails}
                      downloadConsentPDF={downloadConsentPDF}
                      consents={pendingConsents} /> </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}

ProfilePage.propTypes = {
  authData: PropTypes.shape({
    id: PropTypes.number
  }),
  consentRequired: PropTypes.bool,
  downloadConsentPDF: PropTypes.func,
  email: PropTypes.string,
  username: PropTypes.string,
  files: PropTypes.arrayOf(PropTypes.object),
  firstName: PropTypes.string,
  localesList: PropTypes.arrayOf(PropTypes.object),
  selectLanguage: PropTypes.func,
  selectedLocale: PropTypes.string,
  siteName: PropTypes.string,
  strings: PropTypes.objectOf(PropTypes.string),
  studyID: PropTypes.number,
  uploadFile: PropTypes.func,
  showConsentModal: PropTypes.bool
}

export default ProfilePage
